import React, { useEffect, useRef, useState } from 'react';
import { css } from '@emotion/css';
import { StrokeData } from '@remotes/fetchStrokeDataSessions';

interface Props {
  strokeData: StrokeData | null;
  width?: number;
  height?: number;
  className?: string;
}

export const StrokeDataCanvas: React.FC<Props> = ({
  strokeData,
  width = 256,
  height = 256,
  className,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let rafId: number | null = null;

    const renderStrokes = () => {
      setError(null);

      try {
        if (!strokeData) {
          setError('No stroke data available');
          return;
        }

        // Render on canvas
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // Clear canvas
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, width, height);

        // Get canvas size from data or infer from points
        const canvasWidth = strokeData.canvasSize?.width || 1024;
        const canvasHeight = strokeData.canvasSize?.height || 1024;

        // Calculate scaling to fit our display canvas
        const scaleX = width / canvasWidth;
        const scaleY = height / canvasHeight;

        // Draw each stroke
        strokeData.simplifiedStrokes.forEach((stroke) => {
          if (stroke.points.length < 2) return;

          const color = stroke.color || { r: 0, g: 0, b: 0, a: 1 };
          ctx.strokeStyle = `rgba(${color.r}, ${color.g}, ${color.b}, ${color.a})`;
          ctx.lineWidth = 2;
          ctx.lineCap = 'round';
          ctx.lineJoin = 'round';

          ctx.beginPath();
          
          // Move to first point
          const firstPoint = stroke.points[0];
          ctx.moveTo(firstPoint.x * scaleX, firstPoint.y * scaleY);

          // Draw lines to subsequent points
          for (let i = 1; i < stroke.points.length; i++) {
            const point = stroke.points[i];
            ctx.lineTo(point.x * scaleX, point.y * scaleY);
          }

          ctx.stroke();
        });
      } catch (err) {
        console.error('Error rendering stroke data:', err);
        const errorMessage = err instanceof Error ? err.message : 'Failed to render stroke data';
        setError(errorMessage);
      }
    };

    // Use requestAnimationFrame to defer rendering and allow batching
    // This prevents blocking the main thread when many canvases render at once
    rafId = requestAnimationFrame(() => {
      renderStrokes();
    });

    return () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [strokeData, width, height]);

  if (error || !strokeData) {
    return (
      <div className={`${containerStyle} ${className}`}>
        <div className={errorStyle}>{error || 'No data'}</div>
      </div>
    );
  }

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      className={`${canvasStyle} ${className}`}
    />
  );
};

const containerStyle = css`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #ffffff;
  border-radius: 16px;
`;

const canvasStyle = css`
  border-radius: 16px;
  background-color: #ffffff;
`;

const errorStyle = css`
  color: #ff6b6b;
  font-size: 12px;
  padding: 20px;
  text-align: center;
`;

